import {
  commonUtils,
  fileUtils,
  ssnUtils,
  timeUtils,
  validationUtils,
} from '@sato/components';
import * as Yup from 'yup';

import { SerializedPlainDate } from '@models/api/data.types';
import { t, TranslatorFn } from '@stores/localization';
import { FILE_VALIDATION_OPTIONS, MAX_FILE_COUNT } from '@utils/file.utils';

/*
 * Yup error localizations. Must be set before any schema is created, because yup captures the
 * default message when a validator (e.g. `.required()`) is called. Messages are functions so that
 * they are translated at validation time, using the currently selected language.
 */
Yup.setLocale({
  mixed: { required: () => t('error.required_field') },
  string: {
    email: () => t('error.email_not_valid'),
  },
  number: {
    min: ({ min }) => `${t('error.minimum_number')} ${min}`,
  },
});

Yup.addMethod(Yup.array, 'files', function (translateFn: TranslatorFn) {
  return this.test((files, ctx) => {
    if (!files) return true;

    const errors = fileUtils.validateFiles(files, FILE_VALIDATION_OPTIONS);

    if (errors.totalFileSize) {
      return ctx.createError({
        message: translateFn('error.ERROR_TOTAL_FILE_SIZE', {
          maxSize: fileUtils.formatFileSize(
            fileUtils.megabytesToBytes(
              FILE_VALIDATION_OPTIONS.maxTotalFileSizeInMb
            )
          ),
        }),
      });
    }

    if (files.length > MAX_FILE_COUNT) {
      return ctx.createError({
        message: translateFn('error.ERROR_FILE_COUNT', {
          value: MAX_FILE_COUNT,
        }),
      });
    }

    const hasFileErrors = errors.files.some(commonUtils.nonNullable);

    return hasFileErrors || errors.totalFileSize
      ? ctx.createError({ message: translateFn('upload_files_helper') })
      : true;
  });
});

Yup.addMethod(Yup.string, 'ssn', function (message: string) {
  return this.test('ssnFormat', message, validationUtils.isValidSSN);
});

/** Validates that string is a valid SSN and that the person age extracted from the SSN is at least the of the given age */
Yup.addMethod(
  Yup.string,
  'ssnMinAge',
  function (minAge: number, message: string) {
    return this.test('ssnFormat', message, function (value) {
      if (!ssnUtils.isValidSSN(value)) return false;

      const ssnAge = ssnUtils.getAgeFromSSN(value);

      return !!ssnAge && ssnAge >= minAge;
    });
  }
);

Yup.addMethod(Yup.string, 'serializedPlainDate', function (message: string) {
  return this.test(
    'dateFormat',
    message,
    validationUtils.isSerializedPlainDate
  );
});

Yup.addMethod(
  Yup.string,
  'minSerializedDate',
  function (compareDate: SerializedPlainDate | Date, message: string) {
    return this.test('dateFormat', message, (value) =>
      value ? timeUtils.isSameOrAfter(value, compareDate) : true
    );
  }
);

Yup.addMethod(
  Yup.string,
  'maxSerializedDate',
  function (compdareDate: SerializedPlainDate | Date, message: string) {
    return this.test('dateFormat', message, (value) =>
      value ? timeUtils.isSameOrBefore(value, compdareDate) : true
    );
  }
);

Yup.addMethod(
  Yup.string,
  'phoneNumberWithCountryCode',
  function (message: string) {
    return this.test('format', message, function (value: unknown) {
      // Validate only defined values, empty is OK, as required-status is checked elsewhere.
      if (!value) return true;

      return validationUtils.isPhoneNumberWithCountryCode(value);
    });
  }
);
